import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGameEngine } from '../hooks/useGameEngine';
import { ConstructionCanvas } from '../components/game/ConstructionCanvas';
import { ConfidenceMeter } from '../components/game/ConfidenceMeter';
import { PiecePalette } from '../components/game/PiecePalette';
import { ForcedUnknownButton } from '../components/game/ForcedUnknownButton';
import { TutorialOverlay } from '../components/game/TutorialOverlay';
import { ColorPicker } from '../components/game/ColorPicker';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../lib/api';
import { Play, Square, Layers, User, History, LogOut, ChevronDown, HelpCircle, Download, Pause } from 'lucide-react';

export default function GamePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [learning, setLearning] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showTutorial, setShowTutorial] = useState(() => {
    return !localStorage.getItem('fou_tutorial_seen');
  });

  const game = useGameEngine(learning, isPaused);

  const handleCloseTutorial = useCallback(() => {
    setShowTutorial(false);
    localStorage.setItem('fou_tutorial_seen', 'true');
  }, []);

  // Load learning profile and restore won shapes + color preferences
  useEffect(() => {
    api.get('/game/learning')
      .then(res => {
        setLearning(res.data);
        // Restore won shapes from backend
        if (res.data.won_special_shapes && Object.keys(res.data.won_special_shapes).length > 0) {
          game.setWonShapesAvailable(res.data.won_special_shapes);
        }
        // Restore color preferences from backend
        if (res.data.color_preferences && Object.keys(res.data.color_preferences).length > 0) {
          game.setColorPreferences(res.data.color_preferences);
        }
      })
      .catch(() => toast.error('Failed to load learning data'))
      .finally(() => setLoadingData(false));
  }, []);
  
  // Save won shapes to backend when they change
  useEffect(() => {
    if (!learning || Object.keys(game.wonShapesAvailable).length === 0) return;
    
    // Only save if there are actually shapes to save
    const hasShapes = Object.values(game.wonShapesAvailable).some(count => count > 0);
    if (hasShapes) {
      api.post('/game/learning/won-shapes', { won_shapes: game.wonShapesAvailable })
        .catch(err => console.error('Failed to save won shapes:', err));
    }
  }, [game.wonShapesAvailable, learning]);
  
  // Save color preferences to backend when they change
  useEffect(() => {
    if (!learning || Object.keys(game.colorPreferences).length === 0) return;
    
    api.post('/game/learning/color-preferences', { color_preferences: game.colorPreferences })
      .catch(err => console.error('Failed to save color preferences:', err));
  }, [game.colorPreferences, learning]);

  const handleStartSession = useCallback(async () => {
    try {
      const res = await api.post('/game/sessions');
      setSessionId(res.data.id);
      game.startGame();
      toast.success('Session started. Begin building.');
    } catch {
      toast.error('Failed to start session');
    }
  }, [game]);

  const handleEndSession = useCallback(async () => {
    if (!sessionId) return;
    setSaving(true);
    try {
      const data = game.getSessionData();
      await api.put(`/game/sessions/${sessionId}`, data);

      const learningRes = await api.post('/game/learning/update', {
        session_pieces: data.pieces,
        max_confidence: data.max_confidence,
        rounds_survived: data.rounds_survived,
        fundamental_form: data.fundamental_form,
        equilibrium_reached: data.equilibrium_reached || false,
      });

      setLearning(prev => ({
        ...prev,
        total_attempts: learningRes.data.total_attempts,
        total_equilibriums: learningRes.data.total_equilibriums,
        difficulty_modifier: learningRes.data.difficulty_modifier,
        unlocked_pieces: learningRes.data.unlocked_pieces,
        best_round_count: learningRes.data.best_round_count,
      }));

      if (learningRes.data.new_unlock) {
        toast.success('New piece unlocked! Check your palette.');
      }

      toast.success(`Session saved. Rounds survived: ${data.rounds_survived}`);
      game.resetGame();
      setSessionId(null);
      setShowEndDialog(false);
    } catch {
      toast.error('Failed to save session');
    } finally {
      setSaving(false);
    }
  }, [sessionId, game]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
  }, [logout, navigate]);

  // Continue after equilibrium - save round data and continue
  const handleContinueAfterEquilibrium = useCallback(async () => {
    try {
      // Save session data for this round (learning)
      const data = game.getSessionData();
      await api.put(`/game/sessions/${sessionId}`, data);

      const learningRes = await api.post('/game/learning/update', {
        session_pieces: data.pieces,
        max_confidence: data.max_confidence,
        rounds_survived: data.rounds_survived + 1, // Count this round
        fundamental_form: data.fundamental_form,
        equilibrium_reached: true, // Equilibrium WAS reached here
      });

      setLearning(prev => ({
        ...prev,
        total_attempts: learningRes.data.total_attempts,
        total_equilibriums: learningRes.data.total_equilibriums,
        difficulty_modifier: learningRes.data.difficulty_modifier,
        unlocked_pieces: learningRes.data.unlocked_pieces,
        best_round_count: learningRes.data.best_round_count,
      }));

      if (learningRes.data.new_unlock) {
        toast.success('New piece unlocked!');
      }

      // Continue game - this keeps wonShapesAvailable
      game.continueAfterEquilibrium();
      toast.success('Round complete. Continue building!');
    } catch {
      toast.error('Failed to save progress');
    }
  }, [sessionId, game]);

  // End after equilibrium - same as normal end
  const handleEndAfterEquilibrium = useCallback(async () => {
    game.endAfterEquilibrium();
    setShowEndDialog(true);
  }, [game]);

  const unlockedPieces = learning?.unlocked_pieces || ['cube', 'sphere', 'pyramid', 'void', 'pen'];
  const isPlaying = game.phase === 'building' || game.phase === 'destruction';
  
  // Atmospheric styles - subtle environmental feedback, NOT UI targets
  // The world communicates, not text
  const atmosphericStyles = useMemo(() => {
    if (game.equilibriumReached) {
      // Equilibrium: soft, held
      return {
        background: 'bg-zinc-50',
        filter: 'saturate(1.1) brightness(1.02)',
        borderColor: 'border-zinc-100',
      };
    }
    switch (game.atmosphericZone) {
      case 'uncertain':
        // Below 50%: drifting, searching
        return {
          background: 'bg-zinc-50',
          filter: '',
          borderColor: 'border-zinc-200',
        };
      case 'transition':
        // 50-60%: starting to cohere
        return {
          background: 'bg-zinc-50',
          filter: '',
          borderColor: 'border-zinc-200',
        };
      case 'porous':
        // 60-85%: "Ah... this is working"
        return {
          background: 'bg-stone-50',
          filter: 'saturate(1.05)',
          borderColor: 'border-stone-200',
        };
      case 'nervous':
        // Above 85%: Something is wrong...
        return {
          background: 'bg-zinc-50',
          filter: 'saturate(1.05) contrast(1.02)',
          borderColor: 'border-zinc-300',
        };
      default:
        return {
          background: 'bg-zinc-50',
          filter: '',
          borderColor: 'border-zinc-200',
        };
    }
  }, [game.atmosphericZone, game.equilibriumReached]);
  
  // Export structure as image
  const exportStructureAsImage = useCallback(() => {
    const svg = document.querySelector('[data-testid="construction-canvas"] svg');
    if (!svg) {
      toast.error('Could not find canvas');
      return;
    }
    
    // Clone the SVG to avoid modifying the original
    const svgClone = svg.cloneNode(true);
    
    // Get SVG dimensions
    const viewBox = svg.getAttribute('viewBox') || '0 0 800 560';
    const [, , width, height] = viewBox.split(' ').map(Number);
    
    // Create canvas
    const canvas = document.createElement('canvas');
    const scale = 2; // Higher resolution
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    
    // Convert SVG to data URL
    const svgData = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    const img = new Image();
    img.onload = () => {
      ctx.scale(scale, scale);
      ctx.fillStyle = '#FAFAFA';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      
      // Download the image
      const timestamp = new Date().toISOString().split('T')[0];
      const link = document.createElement('a');
      link.download = `open-structure-${timestamp}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Structure exported as image');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error('Failed to export image');
    };
    img.src = url;
  }, []);
  
  // Archive structure handler - now exports as image
  const handleArchive = useCallback(() => {
    const archive = game.archiveStructure();
    exportStructureAsImage();
    console.log('Archived:', archive);
  }, [game, exportStructureAsImage]);

  if (loadingData) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-zinc-400 font-mono text-sm uppercase tracking-widest">Loading field...</div>
      </div>
    );
  }

  return (
    <div 
      className={`h-screen flex flex-col ${atmosphericStyles.background} overflow-hidden`}
      style={{ 
        filter: atmosphericStyles.filter,
        transition: 'filter 0.5s ease',
      }}
      data-testid="game-page"
      data-zone={game.atmosphericZone}
    >
      {/* Equilibrium glow - static, no animation for performance */}
      {game.equilibriumReached && !game.equilibriumFading && (
        <div 
          className="fixed inset-0 pointer-events-none z-40"
          style={{
            background: 'radial-gradient(circle at center, rgba(255,255,255,0.25) 0%, transparent 60%)',
          }}
        />
      )}
      
      {/* Header */}
      <header className={`flex items-center justify-between px-3 lg:px-4 py-2 lg:py-3 border-b ${atmosphericStyles.borderColor} bg-white/80 backdrop-blur-sm`}>
        {/* Left side - Logo on desktop, account dropdown on mobile */}
        <div className="flex items-center gap-2 lg:gap-4">
          {/* Mobile: Account dropdown */}
          <div className="lg:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 px-2" data-testid="mobile-user-menu-btn">
                  <User size={16} />
                  <ChevronDown size={12} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem disabled className="text-xs text-zinc-500">
                  {user?.username}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/history')} data-testid="mobile-menu-history">
                  <History size={14} className="mr-2" /> History
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="mobile-menu-logout">
                  <LogOut size={14} className="mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Desktop: Logo */}
          <h1 className="hidden lg:block text-lg font-bold tracking-tight">Open Structures</h1>
          
          {/* Mobile: Rounds + Difficulty */}
          {isPlaying && (
            <div className="flex items-center gap-2 lg:gap-3">
              <Badge variant="secondary" className="font-mono text-xs gap-1">
                <Layers size={12} /> {game.roundsInSession}
              </Badge>
              {/* Mobile: Difficulty badge */}
              <Badge variant="secondary" className="font-mono text-xs lg:hidden">
                {((learning?.difficulty_modifier || 0) * 100).toFixed(0)}%
              </Badge>
              <Badge variant="secondary" className="font-mono text-xs gap-1 hidden lg:flex">
                <Square size={12} /> {game.totalPiecesPlaced} placed
              </Badge>
            </div>
          )}
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-1 lg:gap-3">
          {/* Mobile: Pause, Help, Export icons */}
          {isPlaying && (
            <Button
              variant={isPaused ? "default" : "ghost"}
              size="sm"
              onClick={() => setIsPaused(!isPaused)}
              className="lg:hidden px-2"
              data-testid="pause-btn-mobile"
            >
              <Pause size={16} className={isPaused ? 'text-white' : ''} />
            </Button>
          )}
          
          {/* Mobile: Help (?) and Export icons */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowTutorial(true)}
            className="lg:hidden px-2"
            data-testid="how-to-play-btn-mobile"
          >
            <HelpCircle size={16} />
          </Button>
          {isPlaying && (
            <Button
              variant="ghost"
              size="sm"
              onClick={exportStructureAsImage}
              className="lg:hidden px-2"
              data-testid="export-btn-mobile"
            >
              <Download size={16} />
            </Button>
          )}
          
          {/* Desktop: Full buttons */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowTutorial(true)}
            className="hidden lg:flex gap-1 text-xs"
            data-testid="how-to-play-btn"
          >
            <HelpCircle size={16} /> How to Play
          </Button>
          
          {isPlaying && (
            <>
              {/* Desktop: Export button */}
              <Button
                variant="outline"
                size="sm"
                onClick={exportStructureAsImage}
                className="hidden lg:flex rounded-sm text-xs uppercase tracking-wider gap-1"
                data-testid="export-btn-desktop"
              >
                <Download size={14} /> Export
              </Button>
              
              {/* Desktop: Pause button */}
              <Button
                variant={isPaused ? "default" : "outline"}
                size="sm"
                onClick={() => setIsPaused(!isPaused)}
                className="hidden lg:flex rounded-sm text-xs uppercase tracking-wider gap-1"
                data-testid="pause-btn"
              >
                <Pause size={14} /> {isPaused ? 'Resume' : 'Pause'}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEndDialog(true)}
                className="hidden lg:flex rounded-sm text-xs uppercase tracking-wider"
                data-testid="end-session-btn"
              >
                <Square size={14} className="mr-1" /> End Session
              </Button>
            </>
          )}

          {/* Desktop: User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="hidden lg:flex gap-2" data-testid="user-menu-btn">
                <User size={16} />
                <span className="text-sm">{user?.username}</span>
                <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/history')} data-testid="menu-history">
                <History size={14} className="mr-2" /> History
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                <LogOut size={14} className="mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Game Area */}
      <div className="flex-1 overflow-hidden">
        {!isPlaying ? (
          /* Idle / Start Screen */
          <div className="h-full flex flex-col items-center justify-center gap-8 p-8">
            <div className="text-center space-y-6 max-w-lg">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
                Open Structures
              </h2>
              <div className="text-base md:text-lg text-zinc-500 leading-relaxed space-y-2">
                <p>Place forms within the field.</p>
                <p>As structure emerges, confidence gathers.</p>
                <p>When certainty hardens, the unknown responds.</p>
                <p className="text-zinc-400 italic">What remains becomes the beginning again.</p>
              </div>
              {learning && learning.total_attempts > 0 && (
                <div className="flex items-center justify-center gap-4 pt-2">
                  <span className="text-xs font-mono text-zinc-400 uppercase">
                    Attempts: {learning.total_attempts}
                  </span>
                  <span className="text-xs font-mono text-zinc-400 uppercase">
                    Best: {learning.best_round_count} rounds
                  </span>
                </div>
              )}
            </div>
            <Button
              onClick={handleStartSession}
              className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-8 py-6 text-lg font-medium gap-2"
              data-testid="start-session-btn"
            >
              <Play size={20} /> Begin
            </Button>
          </div>
        ) : (
          /* Game Layout - responsive for tablet/desktop */
          <div className="h-full flex flex-col lg:grid lg:grid-cols-12 gap-2 lg:gap-3 p-2 lg:p-4 overflow-auto">
            {/* Mobile Layout - ordered: confidence bar + force, then shapes */}
            <div className="flex flex-col lg:hidden gap-2 pb-2 shrink-0">
              {/* Row 1: Confidence bar + Force Unknown */}
              <div className="flex items-center gap-2 w-full">
                {/* Lottery on mobile - show during equilibrium */}
                {game.lotteryActive && (
                  <div className="bg-zinc-900 rounded-sm px-2 py-1 flex items-center gap-1 shrink-0">
                    <span className="text-zinc-400 text-xs font-mono">?</span>
                    <span className="text-lg text-white">
                      {game.lotteryCurrentShape === 'cross' && '✚'}
                      {game.lotteryCurrentShape === 'arrow' && '➤'}
                      {game.lotteryCurrentShape === 'crescent' && '☽'}
                      {game.lotteryCurrentShape === 'bolt' && '⚡'}
                      {game.lotteryCurrentShape === 'wave' && '∿'}
                      {game.lotteryCurrentShape === 'heart' && '♥'}
                      {game.lotteryCurrentShape === 'cloud' && '☁'}
                      {game.lotteryCurrentShape === 'spiral' && '◎'}
                      {game.lotteryCurrentShape === 'fragment' && '◇'}
                      {game.lotteryCurrentShape === 'eye' && '◉'}
                    </span>
                  </div>
                )}
                
                {/* Horizontal Confidence Bar */}
                {(!game.measurementStopped || game.equilibriumFading) ? (
                  <div className="flex items-center gap-2 flex-1">
                    <div className="flex-1 h-6 bg-zinc-100 rounded-sm overflow-hidden relative border border-zinc-200">
                      {/* Unknown pressure zone (from right) */}
                      {game.unknownPressure > 0 && (
                        <div 
                          className="absolute right-0 top-0 h-full bg-zinc-300"
                          style={{ width: `${game.unknownPressure}%` }}
                        />
                      )}
                      {/* Confidence fill */}
                      <div 
                        className="h-full transition-all duration-300"
                        style={{ 
                          width: `${Math.min(game.confidence, 100 - game.unknownPressure)}%`,
                          backgroundColor: game.confidence < 40 ? '#2563EB' : game.confidence < 60 ? '#F59E0B' : game.confidence < 85 ? '#10B981' : '#EF4444'
                        }}
                      />
                      {/* Value overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-mono font-bold text-zinc-700">
                          {Math.round(game.confidence)} / {Math.max(0, Math.round(100 - game.unknownPressure))}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 h-6 bg-zinc-50 rounded-sm flex items-center justify-center">
                    <span className="text-xs font-mono text-zinc-300">—</span>
                  </div>
                )}
                
                {/* Force Unknown OR Archive */}
                {!game.equilibriumReached ? (
                  <button
                    onClick={game.forcedUnknown}
                    disabled={game.phase !== 'building' || game.forcedUnknownCooldown || isPaused}
                    className="bg-orange-600 text-white rounded-sm px-3 py-1.5 font-bold text-xs uppercase tracking-wider disabled:opacity-40 shrink-0"
                    data-testid="forced-unknown-btn-mobile"
                  >
                    {game.forcedUnknownCooldown ? '...' : 'Force'}
                  </button>
                ) : (
                  <button
                    onClick={handleArchive}
                    className="bg-zinc-200 text-zinc-700 rounded-sm px-3 py-1.5 font-bold text-xs uppercase tracking-wider shrink-0"
                    data-testid="archive-btn-mobile"
                  >
                    Save
                  </button>
                )}
              </div>
              
              {/* Row 2: Color Picker */}
              <div className="flex justify-center py-1">
                <ColorPicker
                  selectedColor={game.selectedColor}
                  onSelect={(color) => !isPaused && game.setSelectedColor(color)}
                  testIdPrefix="mobile-"
                />
              </div>
              
              {/* Row 3: Piece palette - wraps to multiple lines, centered */}
              <div className="flex flex-wrap gap-1.5 justify-center w-full">
                <PiecePalette
                  selectedPiece={game.selectedPiece}
                  onSelect={(piece) => !isPaused && game.setSelectedPiece(piece)}
                  unlockedPieces={unlockedPieces}
                  wonShapesAvailable={game.wonShapesAvailable}
                  horizontal
                  testIdPrefix="mobile-"
                  currentConfidence={game.confidence}
                />
              </div>
            </div>

            {/* Desktop: Left Piece Palette */}
            <div className="hidden lg:block lg:col-span-2 overflow-y-auto">
              <PiecePalette
                selectedPiece={game.selectedPiece}
                onSelect={game.setSelectedPiece}
                unlockedPieces={unlockedPieces}
                wonShapesAvailable={game.wonShapesAvailable}
                currentConfidence={game.confidence}
              />
            </div>

            {/* Center: Canvas + Color Picker above (desktop only - mobile has it in mobile layout) */}
            <div className="lg:col-span-7 flex flex-col items-center justify-center flex-1 min-h-0">
              {/* Color Picker - above canvas (desktop only) */}
              <div className="hidden lg:block mb-2 px-2 py-1.5 bg-zinc-50 rounded-sm border border-zinc-200">
                <ColorPicker
                  selectedColor={game.selectedColor}
                  onSelect={(color) => !isPaused && game.setSelectedColor(color)}
                />
              </div>
              
              <ConstructionCanvas
                pieces={game.pieces}
                destroyingPieces={game.destroyingPieces}
                selectedPiece={game.selectedPiece}
                selectedColor={game.selectedColor}
                phase={game.phase}
                onPlace={game.placePiece}
                atmosphericZone={game.atmosphericZone}
                equilibriumReached={game.equilibriumReached}
                equilibriumFading={game.equilibriumFading}
                drawnLines={game.drawnLines}
                currentDrawing={game.currentDrawing}
                isDrawing={game.isDrawing}
                drawingMetrics={game.drawingMetrics}
                onStartDrawing={game.startDrawing}
                onContinueDrawing={game.continueDrawing}
                onEndDrawing={game.endDrawing}
                onRotateTool={game.handleRotateTool}
                isErasing={game.isErasing}
                isNearLineCheck={game.isNearLine}
                onStartErasing={game.startErasing}
                onContinueErasing={game.continueErasing}
                onEndErasing={game.endErasing}
              />
            </div>

            {/* Right: Meter + Controls (desktop only) */}
            <div className="hidden lg:flex lg:col-span-3 flex-col gap-6 items-center">
              <ConfidenceMeter 
                confidence={game.confidence} 
                phase={game.phase} 
                measurementStopped={game.measurementStopped}
                equilibriumReached={game.equilibriumReached}
                equilibriumFading={game.equilibriumFading}
                unknownPressure={game.unknownPressure}
              />
              <Separator className="w-full" />
              
              {/* Force Unknown - withdraws in equilibrium */}
              {!game.equilibriumReached ? (
                <ForcedUnknownButton
                  onClick={game.forcedUnknown}
                  disabled={game.phase !== 'building'}
                  cooldown={game.forcedUnknownCooldown}
                />
              ) : (
                /* Archive Structure - appears in equilibrium */
                <div className="w-full space-y-3 text-center">
                  {/* Lottery Display - flickering shapes during equilibrium */}
                  {game.lotteryActive && (
                    <div className="bg-zinc-900 border border-zinc-700 rounded-sm p-4 animate-pulse">
                      <div className="text-zinc-400 text-xs font-mono uppercase tracking-wider mb-2">
                        {game.lotteryWonShape ? 'Unlocked!' : '? ? ?'}
                      </div>
                      <div className="text-3xl font-bold text-white">
                        {game.lotteryCurrentShape && (
                          <span className="inline-block animate-bounce">
                            {game.lotteryCurrentShape === 'cross' && '✚'}
                            {game.lotteryCurrentShape === 'arrow' && '➤'}
                            {game.lotteryCurrentShape === 'crescent' && '☽'}
                            {game.lotteryCurrentShape === 'bolt' && '⚡'}
                            {game.lotteryCurrentShape === 'wave' && '∿'}
                            {game.lotteryCurrentShape === 'heart' && '♥'}
                            {game.lotteryCurrentShape === 'cloud' && '☁'}
                            {game.lotteryCurrentShape === 'spiral' && '◎'}
                            {game.lotteryCurrentShape === 'fragment' && '◇'}
                            {game.lotteryCurrentShape === 'eye' && '◉'}
                          </span>
                        )}
                      </div>
                      {game.lotteryWonShape && (
                        <div className="text-emerald-400 text-xs font-mono mt-2">
                          +{game.lotteryWonShape}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Show won shape after lottery */}
                  {game.lotteryWonShape && !game.lotteryActive && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                      <div className="text-emerald-600 text-xs font-mono">
                        New shape unlocked: <span className="font-bold">{game.lotteryWonShape}</span>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleArchive}
                    variant="outline"
                    className="w-full rounded-sm gap-2 border-zinc-300 hover:bg-zinc-50"
                    data-testid="archive-structure-btn"
                  >
                    <Download size={16} /> Export as Image
                  </Button>
                  {game.equilibriumFading && (
                    <p className="text-xs text-zinc-400 font-mono">entropy returning...</p>
                  )}
                </div>
              )}
              
              <Separator className="w-full" />
              {/* Pattern Analysis */}
              <div className="w-full space-y-2">
                <span className="text-xs uppercase tracking-widest font-mono text-zinc-400">
                  Pattern Analysis
                </span>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div className="bg-white border border-zinc-200 p-2 rounded-sm">
                    <div className="text-zinc-400">Symmetry</div>
                    <div className="text-zinc-900 font-bold">{game.patternScores.symmetry}%</div>
                  </div>
                  <div className="bg-white border border-zinc-200 p-2 rounded-sm">
                    <div className="text-zinc-400">Density</div>
                    <div className="text-zinc-900 font-bold">{game.patternScores.density}%</div>
                  </div>
                  <div className="bg-white border border-zinc-200 p-2 rounded-sm">
                    <div className="text-zinc-400">Linearity</div>
                    <div className="text-zinc-900 font-bold">{game.patternScores.linearity}%</div>
                  </div>
                  <div className="bg-white border border-zinc-200 p-2 rounded-sm">
                    <div className="text-zinc-400">Regularity</div>
                    <div className="text-zinc-900 font-bold">{game.patternScores.regularity}%</div>
                  </div>
                </div>
                
                {/* Drawing Metrics - shown when pen tool is selected */}
                {game.selectedPiece === 'pen' && (
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono mt-2 pt-2 border-t border-zinc-100">
                    <div className={`p-2 rounded-sm ${game.isDrawing ? 'bg-zinc-900 text-white' : 'bg-zinc-50 border border-zinc-200'}`}>
                      <div className={game.isDrawing ? 'text-zinc-400' : 'text-zinc-400'}>Speed</div>
                      <div className={`font-bold ${game.isDrawing ? (game.drawingMetrics.speed > 300 ? 'text-emerald-400' : 'text-white') : 'text-zinc-400'}`}>
                        {game.isDrawing ? Math.round(game.drawingMetrics.speed) : '—'}
                      </div>
                    </div>
                    <div className={`p-2 rounded-sm ${game.isDrawing ? 'bg-zinc-900 text-white' : 'bg-zinc-50 border border-zinc-200'}`}>
                      <div className={game.isDrawing ? 'text-zinc-400' : 'text-zinc-400'}>Curvature</div>
                      <div className={`font-bold ${game.isDrawing ? (game.drawingMetrics.curvature < 15 ? 'text-emerald-400' : game.drawingMetrics.curvature < 30 ? 'text-amber-400' : 'text-red-400') : 'text-zinc-400'}`}>
                        {game.isDrawing ? `${Math.round(game.drawingMetrics.curvature)}%` : '—'}
                      </div>
                    </div>
                  </div>
                )}
                
                {game.lastChaosEffect && (
                  <div className="text-xs font-mono text-orange-500 mt-1">
                    Last chaos: {game.lastChaosEffect}
                  </div>
                )}
              </div>
              <Separator className="w-full" />
              {/* Learning info */}
              <div className="w-full space-y-2">
                <span className="text-xs uppercase tracking-widest font-mono text-zinc-400">
                  Learning
                </span>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div className="bg-white border border-zinc-200 p-2 rounded-sm">
                    <div className="text-zinc-400">Difficulty</div>
                    <div className="text-zinc-900 font-bold">{((learning?.difficulty_modifier || 0) * 100).toFixed(0)}%</div>
                  </div>
                  <div className="bg-white border border-zinc-200 p-2 rounded-sm">
                    <div className="text-zinc-400">Attempts</div>
                    <div className="text-zinc-900 font-bold">{learning?.total_attempts || 0}</div>
                  </div>
                  <div className="bg-white border border-zinc-200 p-2 rounded-sm">
                    <div className="text-zinc-400">Best</div>
                    <div className="text-zinc-900 font-bold">{learning?.best_round_count || 0} rnd</div>
                  </div>
                  <div className="bg-white border border-zinc-200 p-2 rounded-sm">
                    <div className="text-zinc-400">Equilib.</div>
                    <div className="text-zinc-900 font-bold">{learning?.total_equilibriums || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tutorial */}
      <TutorialOverlay open={showTutorial} onClose={handleCloseTutorial} />

      {/* End Session Dialog */}
      <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>End Session?</DialogTitle>
            <DialogDescription>
              Your progress will be saved and the game will learn from this attempt.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 py-4 text-center font-mono">
            <div>
              <div className="text-2xl font-bold">{game.roundsInSession}</div>
              <div className="text-xs text-zinc-400 uppercase">Rounds</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{game.totalPiecesPlaced}</div>
              <div className="text-xs text-zinc-400 uppercase">Pieces</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{Math.round(game.maxConfidence)}</div>
              <div className="text-xs text-zinc-400 uppercase">Max Conf.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEndDialog(false)} className="rounded-sm" data-testid="cancel-end-btn">
              Continue Playing
            </Button>
            <Button onClick={handleEndSession} disabled={saving} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm" data-testid="confirm-end-btn">
              {saving ? 'Saving...' : 'End & Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Equilibrium Congratulations Popup */}
      <Dialog open={game.showEquilibriumPopup} onOpenChange={() => {}}>
        <DialogContent className="rounded-sm max-w-md" hideClose>
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">Equilibrium</DialogTitle>
            <DialogDescription className="text-center text-base">
              You have found balance within the field.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 space-y-4">
            {/* Won shape display */}
            {game.lotteryWonShape && (
              <div className="text-center bg-zinc-50 border border-zinc-200 rounded-sm p-4">
                <div className="text-xs font-mono text-zinc-400 uppercase tracking-wider mb-2">
                  Shape Granted
                </div>
                <div className="text-4xl mb-2">
                  {game.lotteryWonShape === 'cross' && '✚'}
                  {game.lotteryWonShape === 'arrow' && '➤'}
                  {game.lotteryWonShape === 'crescent' && '☽'}
                  {game.lotteryWonShape === 'bolt' && '⚡'}
                  {game.lotteryWonShape === 'wave' && '∿'}
                  {game.lotteryWonShape === 'heart' && '♥'}
                  {game.lotteryWonShape === 'cloud' && '☁'}
                  {game.lotteryWonShape === 'spiral' && '◎'}
                  {game.lotteryWonShape === 'fragment' && '◇'}
                  {game.lotteryWonShape === 'eye' && '◉'}
                </div>
                <div className="text-lg font-bold capitalize">{game.lotteryWonShape}</div>
                <div className="text-sm text-emerald-600 font-mono">×{game.lotteryWonCount}</div>
              </div>
            )}
            
            {/* Round stats */}
            <div className="grid grid-cols-3 gap-4 text-center font-mono">
              <div>
                <div className="text-2xl font-bold">{game.roundsInSession + 1}</div>
                <div className="text-xs text-zinc-400 uppercase">Round</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{game.totalPiecesPlaced}</div>
                <div className="text-xs text-zinc-400 uppercase">Pieces</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{Math.round(game.maxConfidence)}</div>
                <div className="text-xs text-zinc-400 uppercase">Max</div>
              </div>
            </div>
            
            {/* Collected special shapes summary */}
            {Object.keys(game.wonShapesAvailable).length > 0 && (
              <div className="text-center text-xs font-mono text-zinc-500">
                Special shapes collected: {Object.entries(game.wonShapesAvailable)
                  .filter(([, count]) => count > 0)
                  .map(([shape, count]) => `${shape} ×${count}`)
                  .join(', ') || 'none'}
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={handleEndAfterEquilibrium} 
              className="rounded-sm flex-1"
              data-testid="equilibrium-end-btn"
            >
              End Session
            </Button>
            <Button 
              onClick={handleContinueAfterEquilibrium} 
              className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm flex-1"
              data-testid="equilibrium-continue-btn"
            >
              Continue Building
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
